import { useState } from 'react';
import styled, { css } from 'styled-components';
import { FormikHelpers } from 'formik';

import { CopyIcon } from 'shared/icons/CopyIcon';
import { Checkmark } from 'shared/icons/Checkmark';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';
import EmailValidation, { IEmailEntryFormValue } from './EmailValidation';

const TP = 'giftCards.components.recipientLandingPage';

interface CodeProps {
  code: string;
  fadeIn?: boolean;
  isEmailEntryRequired?: boolean;
  isValidating?: boolean;
  handleSubmit: (
    values: IEmailEntryFormValue,
    actions: FormikHelpers<IEmailEntryFormValue>,
  ) => Promise<void>;
}

export const Code = ({
  code,
  fadeIn = false,
  isEmailEntryRequired = false,
  isValidating = false,
  handleSubmit,
}: CodeProps) => {
  const [textCopied, setTextCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code).then(
      () => {
        /* clipboard successfully set */
        setTextCopied(true);
        setTimeout(() => {
          setTextCopied(false);
        }, 3000);
      },
      () => {
        /* clipboard write failed */
        setTextCopied(false);
      },
    );

    sendTrackingEvent('GC_CODE_COPY_TAP');
  };

  if (isEmailEntryRequired && !fadeIn && !code) {
    return <EmailValidation isValidating={isValidating} handleSubmit={handleSubmit} code={code} />;
  }

  if (code) {
    return (
      <Code.Wrapper $fadeIn={fadeIn}>
        <Code.Code>
          {!textCopied ? (
            <div>
              {code}
              <Code.CopyButton onClick={handleCopyCode}>
                <CopyIcon fill="white" />
              </Code.CopyButton>
            </div>
          ) : (
            <Code.CopiedText>
              <Checkmark color="white" />
              &nbsp; {t(`${TP}.copied`, 'Copied')}
            </Code.CopiedText>
          )}
        </Code.Code>

        <Code.LearnMore $fadeIn={fadeIn}>
          {t(`${TP}.applyCodeDuringCheckout`, 'Apply this code during checkout on GOAT.com.')}
          <Code.LearnMoreLink
            href="https://support.goat.com/hc/en-us/categories/11113687728013-Gift-Cards"
            target="_blank"
            rel="noreferrer"
          >
            {t(`${TP}.learnMore`, 'Learn More')}
          </Code.LearnMoreLink>
        </Code.LearnMore>
      </Code.Wrapper>
    );
  }

  return null;
};

Code.Wrapper = styled.div<{ $fadeIn: boolean }>`
  ${({ $fadeIn }) =>
    $fadeIn &&
    css`
      display: flex;
      flex-direction: row;
      margin-left: 32px;
      animation: fadein 4s;

      @keyframes fadein {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `}
`;
Code.Code = styled.div`
  margin: 0px 16px 8px 0px;
  ${variableFontRegular({ size: '13.5px', height: '17px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    ${variableFontRegular({ size: '11px', height: '13px' })};
  }
`;

Code.CopiedText = styled.div`
  ${variableFontRegular({ size: '13.5px', height: '23.5px' })};
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    ${variableFontRegular({ size: '11px', height: '18.5px' })};
  }
`;
Code.CopyButton = styled.button`
  margin-left: 8px;
`;

Code.LearnMore = styled.div<{ $fadeIn?: boolean }>`
  ${({ $fadeIn }) =>
    !$fadeIn
      ? variableFontRegular({ size: '9.5px', height: '9.5px' })
      : variableFontRegular({ size: '9.5px', height: '22.5px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    ${({ $fadeIn }) =>
      !$fadeIn
        ? variableFontRegular({ size: '9px', height: '9px' })
        : variableFontRegular({ size: '9px', height: '22.5px' })};
  }
`;

Code.LearnMoreLink = styled.a`
  padding-left: 2px;
  text-decoration: underline;
  cursor: pointer;
`;
